package com.patronat.top.features.venues.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.dp
import com.patronat.top.core.domain.models.profiles.User
import com.patronat.top.dto.UserRole
import com.patronat.top.ui.molecules.filters.FilterOption
import com.patronat.top.ui.organisms.EmptyStateContent
import com.patronat.top.ui.organisms.UsersControlPanel
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.MineShaft50
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.venue_list_add_button
import top.features.venues.generated.resources.venue_list_empty_description
import top.features.venues.generated.resources.venue_list_empty_title
import top.features.venues.generated.resources.venue_list_filter_status
import top.features.venues.generated.resources.venue_list_search_placeholder
import top.features.venues.generated.resources.venue_status_added_by_moderator
import top.features.venues.generated.resources.venue_status_application
import top.features.venues.generated.resources.venue_status_check_changes
import top.features.venues.generated.resources.venue_status_closed
import top.features.venues.generated.resources.venue_status_connected
import top.features.venues.generated.resources.venue_status_draft
import top.features.venues.generated.resources.venue_status_pending_review
import top.features.venues.generated.resources.venue_status_rejected

@Composable
fun VenuesScreen(
    currentUser: User,
    emptyStateImage: Painter,
    modifier: Modifier = Modifier,
    pickServicesPdfFile: suspend () -> VenuePickedFile? = { null },
    pickGalleryImageFile: suspend () -> VenuePickedFile? = { null }
) {
    val viewModel = koinInject<VenuesListViewModel>()
    val state by viewModel.viewState.collectAsState()
    var formMode by remember { mutableStateOf<VenueFormMode?>(null) }

    val searchPlaceholder = stringResource(Res.string.venue_list_search_placeholder)
    val filterStatusLabel = stringResource(Res.string.venue_list_filter_status)
    val addVenueButtonText = stringResource(Res.string.venue_list_add_button)
    val emptyTitle = stringResource(Res.string.venue_list_empty_title)
    val emptyDescription = stringResource(Res.string.venue_list_empty_description)

    val statusOptions = listOf(
        FilterOption(VenueStatusFilterPreset.ON_MODERATION.id, stringResource(Res.string.venue_status_pending_review)),
        FilterOption(VenueStatusFilterPreset.APPLICATION.id, stringResource(Res.string.venue_status_application)),
        FilterOption(VenueStatusFilterPreset.CONNECTED.id, stringResource(Res.string.venue_status_connected)),
        FilterOption(VenueStatusFilterPreset.REJECTED.id, stringResource(Res.string.venue_status_rejected)),
        FilterOption(VenueStatusFilterPreset.ADDED_BY_MODERATOR.id, stringResource(Res.string.venue_status_added_by_moderator)),
        FilterOption(VenueStatusFilterPreset.CHECK_CHANGES.id, stringResource(Res.string.venue_status_check_changes)),
        FilterOption(VenueStatusFilterPreset.CLOSED.id, stringResource(Res.string.venue_status_closed)),
        FilterOption(VenueStatusFilterPreset.DRAFT.id, stringResource(Res.string.venue_status_draft))
    )
    val selectedStatusOptions = statusOptions.filter { option ->
        state.selectedStatusFilterIds.any { it == option.id }
    }

    LaunchedEffect(currentUser.role, currentUser.id) {
        viewModel.onEvent(
            VenuesListEvent.Initialize(
                userRole = currentUser.role,
                currentUserId = currentUser.id
            )
        )
    }

    LaunchedEffect(viewModel) {
        viewModel.effect.collect { effect ->
            when (effect) {
                VenuesListEffect.OpenCreateVenue -> formMode = VenueFormMode.Create
                is VenuesListEffect.OpenCreateVenueFromAi -> formMode = VenueFormMode.CreateFromAi(effect.draft)
                is VenuesListEffect.OpenEditVenue -> formMode = VenueFormMode.Edit(effect.venueId)
            }
        }
    }

    AddVenueActionDialog(
        visible = state.isAddVenueDialogVisible,
        mode = state.addVenueActionMode,
        query = state.addVenueSearchQuery,
        candidates = state.addVenueCandidates,
        selectedCandidateId = state.selectedAddVenueCandidateId,
        isLoading = state.isAddVenueCandidatesLoading || state.isAddVenueAiLoading,
        canUseAiImport = currentUser.role == UserRole.MODERATOR || currentUser.role == UserRole.SUPER_ADMIN,
        aiWebsiteUrl = state.addVenueAiWebsiteUrl,
        aiCreateImmediately = state.addVenueAiCreateImmediately,
        onDismiss = { viewModel.onEvent(VenuesListEvent.OnDismissAddVenueDialog) },
        onModeChanged = { mode -> viewModel.onEvent(VenuesListEvent.OnAddVenueActionModeChanged(mode)) },
        onQueryChanged = { query -> viewModel.onEvent(VenuesListEvent.OnAddVenueCandidateQueryChanged(query)) },
        onCandidateSelected = { venueId -> viewModel.onEvent(VenuesListEvent.OnAddVenueCandidateSelected(venueId)) },
        onAiWebsiteUrlChanged = { url -> viewModel.onEvent(VenuesListEvent.OnAddVenueAiWebsiteChanged(url)) },
        onAiCreateImmediatelyChanged = { checked ->
            viewModel.onEvent(VenuesListEvent.OnAddVenueAiCreateImmediatelyChanged(checked))
        },
        onConfirm = { viewModel.onEvent(VenuesListEvent.OnConfirmAddVenueAction) }
    )

    if (formMode != null) {
        VenueFormRoute(
            mode = formMode!!,
            currentUserRole = currentUser.role,
            onBack = {
                formMode = null
                viewModel.onEvent(VenuesListEvent.OnReload)
            },
            modifier = modifier,
            pickServicesPdfFile = pickServicesPdfFile,
            pickGalleryImageFile = pickGalleryImageFile
        )
        return
    }

    if (state.isEmpty) {
        EmptyStateContent(
            image = emptyStateImage,
            title = emptyTitle,
            description = emptyDescription,
            buttonText = addVenueButtonText,
            onButtonClick = { viewModel.onEvent(VenuesListEvent.OnAddVenueClicked) },
            buttonIcon = AppIcons.Plus,
            buttonNeeded = true,
            modifier = modifier.background(MineShaft50)
        )
        return
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
    ) {
        UsersControlPanel(
            searchQuery = state.searchQuery,
            onSearchQueryChange = { query ->
                viewModel.onEvent(VenuesListEvent.OnSearchChanged(query))
            },
            searchPlaceholder = searchPlaceholder,
            filterLabel = filterStatusLabel,
            filterOptions = statusOptions,
            selectedFilterOptions = selectedStatusOptions,
            onFilterChange = { selected ->
                viewModel.onEvent(
                    VenuesListEvent.OnStatusFilterChanged(
                        selected.map { it.id }
                    )
                )
            },
            onAddClick = { viewModel.onEvent(VenuesListEvent.OnAddVenueClicked) },
            addButtonText = addVenueButtonText,
            modifier = Modifier.padding(bottom = 24.dp)
        )

        VenuesTable(
            venues = state.venues,
            venueModeratorsById = state.moderatorsById,
            venueOwnersById = state.ownersById,
            totalCount = state.totalCount,
            currentPage = state.currentPage,
            pageSize = state.pageSize,
            expandedVenueMenuId = state.expandedVenueMenuId,
            canBlockVenue = currentUser.role == UserRole.MODERATOR || currentUser.role == UserRole.SUPER_ADMIN,
            onPageChange = { page -> viewModel.onEvent(VenuesListEvent.OnPageChanged(page)) },
            onPageSizeChange = { pageSize -> viewModel.onEvent(VenuesListEvent.OnPageSizeChanged(pageSize)) },
            onActionClick = { venueId -> viewModel.onEvent(VenuesListEvent.OnVenueActionClicked(venueId)) },
            onDismissMenu = { viewModel.onEvent(VenuesListEvent.OnDismissVenueMenu) },
            onEditVenue = { venueId -> viewModel.onEvent(VenuesListEvent.OnEditVenueClicked(venueId)) },
            onBlockVenue = { venueId -> viewModel.onEvent(VenuesListEvent.OnBlockVenueClicked(venueId)) }
        )
    }
}
